import * as React from 'react';
import * as SwitchPrimitive from '@radix-ui/react-switch';
import { cn } from '../../lib/utils';
import { useTranslation } from 'react-i18next';

export interface SwitchProps
  extends Omit<
    React.ComponentPropsWithoutRef<typeof SwitchPrimitive.Root>,
    'checked' | 'onCheckedChange' | 'onChange' | 'children'
  > {
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  label?: React.ReactNode;
  description?: React.ReactNode;
  /** Render the live state next to the track — the isActive rows read better with it. */
  showStateText?: boolean;
  onLabel?: string;
  offLabel?: string;
  /** Classes for the row; `className` styles the track itself. */
  containerClassName?: string;
}

/**
 * The isActive toggle used across every settings screen. Sized to the ERP
 * density (16px track) rather than the usual 24px consumer switch.
 */
export const Switch = React.forwardRef<React.ElementRef<typeof SwitchPrimitive.Root>, SwitchProps>(
  function Switch(
    {
      checked,
      onChange,
      label,
      description,
      showStateText = false,
      onLabel: onLabelProp,
      offLabel: offLabelProp,
      disabled,
      className,
      containerClassName,
      id,
      ...props
    },
    ref,
  ) {
    const { t } = useTranslation('common');
    const onLabel = onLabelProp ?? t('status.active');
    const offLabel = offLabelProp ?? t('status.inactive');
    const generatedId = React.useId();
    const controlId = id ?? generatedId;
    const descriptionId = `${controlId}-desc`;

    const control = (
      <SwitchPrimitive.Root
        ref={ref}
        id={controlId}
        disabled={disabled}
        checked={checked}
        onCheckedChange={(next) => onChange?.(next)}
        aria-describedby={description ? descriptionId : undefined}
        data-testid="switch"
        className={cn(
          'inline-flex h-4 w-7 shrink-0 items-center rounded-full bg-slate-300 transition-colors',
          'focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500 focus-visible:ring-offset-1',
          'data-[state=checked]:bg-blue-600',
          'disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
        {...props}
      >
        {/* DS-50 — Radix does not flip the thumb, so the travel is mirrored by
            hand; the rtl: variants outrank the LTR ones on specificity. */}
        <SwitchPrimitive.Thumb
          className={cn(
            'pointer-events-none block h-3 w-3 rounded-full bg-white shadow-sm transition-transform',
            'translate-x-0.5 data-[state=checked]:translate-x-3.5',
            'rtl:-translate-x-0.5 rtl:data-[state=checked]:-translate-x-3.5',
          )}
        />
      </SwitchPrimitive.Root>
    );

    const stateText = showStateText ? (
      <span
        className={cn(
          'text-[10px] font-bold uppercase tracking-widest',
          disabled ? 'text-slate-400' : checked ? 'text-emerald-600' : 'text-slate-400',
        )}
      >
        {checked ? onLabel : offLabel}
      </span>
    ) : null;

    if (!label && !description && !stateText) return control;

    return (
      <div className={cn('flex items-start gap-2 text-xs', containerClassName)}>
        <span className="flex h-[18px] items-center">{control}</span>
        <span className="min-w-0">
          {label || stateText ? (
            <label
              htmlFor={controlId}
              className={cn(
                'flex items-center gap-1.5 leading-[18px]',
                disabled ? 'cursor-not-allowed text-slate-400' : 'cursor-pointer text-slate-700',
              )}
            >
              {label}
              {stateText}
            </label>
          ) : null}
          {description ? (
            <span id={descriptionId} className="mt-0.5 block text-[11px] leading-tight text-slate-500">
              {description}
            </span>
          ) : null}
        </span>
      </div>
    );
  },
);
